import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { AuthContext } from '../context/auth-context';
import ProjectEmptyState from '../projects/ProjectEmptyState';
import AnalyticsAPI from '../../services/api/analytics-api';
import LaborKpiStrip from './LaborKpiStrip';
import PackageLaborGrid from './PackageLaborGrid';
import PackageLaborChart from './PackageLaborChart';
import PackageLaborDrawer from './PackageLaborDrawer';
import ShopSyncExceptionChip from '../shared/ShopSyncExceptionChip';
import {
  addCalendarDays,
  getCurrentWeekMondayYmd,
  parseLocalCalendarDate,
} from '../../utils/field-start-date-policy';

// ─── Helpers ────────────────────────────────────────────────────────────────

const getCurrentMonday = () => getCurrentWeekMondayYmd();

const formatWeekLabel = (weekStartStr) => {
  const d = parseLocalCalendarDate(weekStartStr);
  const end = new Date(d);
  end.setDate(end.getDate() + 6);
  const mo = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${mo[d.getMonth()]} ${d.getDate()} – ${mo[end.getMonth()]} ${end.getDate()}`;
};

// ─── Component ───────────────────────────────────────────────────────────────

const Analytics = () => {
  const auth = useContext(AuthContext);

  // Global project selection from Redux (set via the sidebar drawer).
  const selectedProject = useSelector((state) => state.project.selectedProject);
  const selectedProjectId = selectedProject?.id || null;

  // ── State ──────────────────────────────────────────────────────────────────
  const [selectedPmId, setSelectedPmId] = useState('all');
  const [pms, setPms] = useState([]);
  const [mode, setMode] = useState('week'); // 'week' | 'all'
  const [weekStart, setWeekStart] = useState(getCurrentMonday());
  const [view, setView] = useState('grid'); // 'grid' | 'chart'
  const [packages, setPackages] = useState([]);
  const [expandedPackageId, setExpandedPackageId] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [drawerDetail, setDrawerDetail] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const isCurrentWeek = weekStart === getCurrentMonday();

  // ── Week navigation ────────────────────────────────────────────────────────
  const navigateWeek = (direction) => {
    setWeekStart(addCalendarDays(weekStart, direction === 'prev' ? -7 : 7));
  };

  // ── Data fetching ──────────────────────────────────────────────────────────

  // Reset PM/search/status filters when the global project changes.
  useEffect(() => {
    setSelectedPmId('all');
    setSearch('');
    setStatusFilter('all');
  }, [selectedProjectId]);

  // Load PMs when project changes
  useEffect(() => {
    if (!selectedProjectId) {
      setPms([]);
      return;
    }
    const loadPms = async () => {
      try {
        const data = await AnalyticsAPI.getProjectPms(
          selectedProjectId,
          auth.token
        );
        setPms(data.pms || []);
      } catch (err) {
        setPms([]);
      }
    };
    loadPms();
  }, [selectedProjectId, auth.token]);

  // Load package labor summary — reusable so we can trigger a manual refresh
  // (e.g. after a timecard correction) without changing weekStart/project.
  const loadPackages = useCallback(
    async ({ silent = false } = {}) => {
      if (!selectedProjectId) {
        setPackages([]);
        return;
      }
      if (!silent) setLoading(true);
      setError(null);
      try {
        const data = await AnalyticsAPI.getPackageLaborSummary(
          selectedProjectId,
          weekStart,
          auth.token
        );
        setPackages(data.packages || []);
      } catch (err) {
        setError('Failed to load package data');
        setPackages([]);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [selectedProjectId, weekStart, auth.token]
  );

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  // After a timecard correction, refetch the summary AND bump the detail-panel
  // refresh key so any currently-expanded PackageLaborDetail refetches its
  // employee×day data for both the source and target packages.
  const handleCorrectionSuccess = useCallback(() => {
    loadPackages({ silent: true });
    setDetailRefreshKey((k) => k + 1);
  }, [loadPackages]);

  // Load drawer detail when selected package changes (chart view)
  useEffect(() => {
    if (!selectedPackage) {
      setDrawerDetail(null);
      return;
    }
    const loadDetail = async () => {
      setDrawerLoading(true);
      try {
        const data = await AnalyticsAPI.getPackageLaborDetail(
          selectedPackage.package_id,
          weekStart,
          auth.token
        );
        setDrawerDetail(data);
      } catch (err) {
        setDrawerDetail(null);
      } finally {
        setDrawerLoading(false);
      }
    };
    loadDetail();
  }, [selectedPackage, weekStart, auth.token]);

  // Clear expanded/selected state when context changes
  useEffect(() => {
    setExpandedPackageId(null);
    setSelectedPackage(null);
    setDrawerDetail(null);
  }, [selectedProjectId, selectedPmId, mode, weekStart, view]);

  // ── Enriched rows (computed from raw packages) ─────────────────────────────
  const enriched = packages.map((pkg) => {
    const combinedAlltime =
      (pkg.field_hrs_alltime || 0) + (pkg.shop_hrs_alltime || 0);
    const combinedBudget =
      (pkg.field_hrs_budgeted || 0) + (pkg.shop_hrs_budgeted || 0);
    const budgetPctCombined =
      combinedBudget > 0
        ? Math.round((combinedAlltime / combinedBudget) * 100)
        : null;
    const combinedEarned =
      (pkg.earned_field_hrs || 0) + (pkg.earned_shop_hrs || 0);
    const earnedPctCombined =
      combinedBudget > 0
        ? Math.round((combinedEarned / combinedBudget) * 100)
        : null;
    const weekHrsTotal = (pkg.field_hrs_week || 0) + (pkg.shop_hrs_week || 0);

    const fieldOver =
      pkg.field_hrs_budgeted && pkg.field_hrs_alltime > pkg.field_hrs_budgeted;
    const shopOver =
      pkg.shop_hrs_budgeted && pkg.shop_hrs_alltime > pkg.shop_hrs_budgeted;
    const healthStatus =
      fieldOver ||
      shopOver ||
      (budgetPctCombined !== null && budgetPctCombined >= 100)
        ? 'over'
        : budgetPctCombined !== null && budgetPctCombined >= 85
          ? 'approaching'
          : 'ok';

    return {
      ...pkg,
      combinedAlltime,
      combinedBudget,
      budgetPctCombined,
      combinedEarned,
      earnedPctCombined,
      weekHrsTotal,
      healthStatus,
    };
  });

  // ── Filtering ──────────────────────────────────────────────────────────────
  // baseRows: enriched rows filtered by PM and mode.
  let baseRows = enriched;
  if (selectedPmId !== 'all') {
    baseRows = baseRows.filter((r) => r.pm_employee_id === selectedPmId);
  }
  if (mode === 'week') {
    baseRows = baseRows.filter((r) => r.weekHrsTotal > 0);
  }

  // statusOptions: ['all', ...unique tracking_status_label values from baseRows]
  const statusOptions = [
    'all',
    ...new Set(baseRows.map((r) => r.tracking_status_label).filter(Boolean)),
  ];

  // visibleRows: baseRows filtered by search text and selected status.
  const visibleRows = baseRows.filter((pkg) => {
    const matchSearch =
      !search ||
      pkg.autodesk_name?.toLowerCase().includes(search.toLowerCase()) ||
      pkg.description?.toLowerCase().includes(search.toLowerCase()) ||
      pkg.iwp?.toLowerCase().includes(search.toLowerCase()) ||
      pkg.area_name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      statusFilter === 'all' || pkg.tracking_status_label === statusFilter;
    return matchSearch && matchStatus;
  });

  // Default sort: highest HRS WEEK first (matches grid default sort)
  // sortedRows: sorted copy of visibleRows by weekHrsTotal descending.
  const sortedRows = [...visibleRows].sort((a, b) => {
    const aW = a.weekHrsTotal ?? 0;
    const bW = b.weekHrsTotal ?? 0;
    return bW - aW;
  });

  // ── KPI values ─────────────────────────────────────────────────────────────
  const overBudgetCount = visibleRows.filter(
    (r) => r.healthStatus === 'over'
  ).length;
  const approachingCount = visibleRows.filter(
    (r) => r.healthStatus === 'approaching'
  ).length;
  const totalWeekHrs = visibleRows.reduce((sum, r) => sum + r.weekHrsTotal, 0);
  const totalAllHrs = visibleRows.reduce(
    (sum, r) => sum + r.combinedAlltime,
    0
  );

  // ── Styles ─────────────────────────────────────────────────────────────────
  const pillToggleBase = {
    padding: '6px 16px',
    borderRadius: 20,
    border: 'none',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    transition: 'background 0.15s, color 0.15s',
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (!selectedProjectId) {
    return <ProjectEmptyState />;
  }

  return (
    <div
      style={{
        backgroundColor: '#f1f5f9',
        minHeight: '100vh',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {/* ── Header / filters — Project, PM, Grid·Chart ── */}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: 10,
          padding: '12px 16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
            flex: '1 1 auto',
            minWidth: 0,
          }}
        >
          {/* PM filter chips */}
          {selectedProjectId && pms.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                }}
              >
                PM
              </span>
              {[
                { employee_id: 'all', firstname: 'All', lastname: 'PMs' },
                ...pms,
              ].map((pm) => {
                const isSelected = selectedPmId === pm.employee_id;
                return (
                  <button
                    key={pm.employee_id}
                    onClick={() => setSelectedPmId(pm.employee_id)}
                    style={{
                      padding: '4px 12px',
                      borderRadius: 20,
                      border: `1.5px solid ${isSelected ? '#3b82f6' : '#e2e8f0'}`,
                      backgroundColor: isSelected ? '#eff6ff' : '#fff',
                      color: isSelected ? '#1d4ed8' : '#64748b',
                      fontWeight: isSelected ? 700 : 400,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    {pm.employee_id === 'all'
                      ? 'All PMs'
                      : `${pm.firstname} ${pm.lastname}`}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexShrink: 0,
          }}
        >
          <ShopSyncExceptionChip
            projectId={selectedProjectId}
            projectName={selectedProject?.name || ''}
            token={auth.token}
            canResolve={!!auth.isAdmin}
          />

          {/* View toggle — Grid / Chart */}
          <div
            style={{
              display: 'flex',
              gap: 4,
              backgroundColor: '#f8fafc',
              borderRadius: 8,
              padding: 4,
              border: '1px solid #e2e8f0',
              flexShrink: 0,
            }}
          >
            {[
              ['grid', '⊞ Grid'],
              ['chart', '▤ Chart'],
            ].map(([v, label]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                style={{
                  ...pillToggleBase,
                  borderRadius: 6,
                  padding: '6px 14px',
                  backgroundColor: view === v ? '#1e293b' : 'transparent',
                  color: view === v ? '#fff' : '#64748b',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content — only shown after project is selected ── */}
      {selectedProjectId && (
        <>
          {/* Mode toggle + Week navigator */}
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: 10,
              padding: '10px 16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            {/* Mode pill toggle */}
            <div
              style={{
                display: 'flex',
                backgroundColor: '#f1f5f9',
                borderRadius: 22,
                padding: 3,
                gap: 2,
              }}
            >
              {[
                ['week', 'By Week'],
                ['all', 'Project to Date'],
              ].map(([m, label]) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  style={{
                    ...pillToggleBase,
                    backgroundColor: mode === m ? '#1e293b' : 'transparent',
                    color: mode === m ? '#fff' : '#64748b',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Week navigator — only for By Week */}
            {mode === 'week' && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <button
                  onClick={() => navigateWeek('prev')}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 6,
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    fontSize: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#475569',
                  }}
                >
                  ‹
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}
                  >
                    {formatWeekLabel(weekStart)}
                  </span>
                  {isCurrentWeek && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#2563eb',
                        backgroundColor: '#eff6ff',
                        padding: '2px 8px',
                        borderRadius: 10,
                      }}
                    >
                      Current
                    </span>
                  )}
                </div>

                <button
                  onClick={() => navigateWeek('next')}
                  disabled={isCurrentWeek}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 6,
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#fff',
                    cursor: isCurrentWeek ? 'not-allowed' : 'pointer',
                    fontSize: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isCurrentWeek ? '#cbd5e1' : '#475569',
                  }}
                >
                  ›
                </button>
              </div>
            )}
          </div>

          {/* Shared Search and Status Toolbar */}
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: 10,
              padding: '12px 16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search description, IWP, area..."
              style={{
                padding: '7px 12px',
                borderRadius: 6,
                border: '1px solid #e2e8f0',
                fontSize: 13,
                minWidth: 220,
                color: '#1e293b',
                outline: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {statusOptions.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 20,
                    border: '1px solid #e2e8f0',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    backgroundColor: statusFilter === s ? '#1e293b' : '#fff',
                    color: statusFilter === s ? '#fff' : '#64748b',
                  }}
                >
                  {s === 'all' ? 'All' : s}
                </button>
              ))}
            </div>
            <span
              style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}
            >
              {visibleRows.length} of {baseRows.length} packages
            </span>
          </div>

          {/* KPI Strip */}
          <LaborKpiStrip
            packagesShown={visibleRows.length}
            overBudget={overBudgetCount}
            approaching={approachingCount}
            hoursValue={mode === 'week' ? totalWeekHrs : totalAllHrs}
            hoursLabel={mode === 'week' ? 'Hours This Week' : 'Total Hours'}
            hoursSub={mode === 'week' ? formatWeekLabel(weekStart) : 'all time'}
          />

          {/* Loading / Error */}
          {loading && (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
              Loading...
            </div>
          )}
          {error && !loading && (
            <div style={{ textAlign: 'center', padding: 40, color: '#ef4444' }}>
              {error}
            </div>
          )}

          {/* Grid or Chart view */}
          {!loading && !error && view === 'grid' && (
            <PackageLaborGrid
              rows={sortedRows}
              mode={mode}
              weekStart={weekStart}
              token={auth.token}
              isAdmin={auth.isAdmin}
              projectId={selectedProjectId}
              detailRefreshKey={detailRefreshKey}
              onCorrectionSuccess={handleCorrectionSuccess}
            />
          )}
          {!loading && !error && view === 'chart' && (
            <PackageLaborChart
              rows={sortedRows}
              mode={mode}
              onPackageSelect={(pkg) => setSelectedPackage(pkg)}
              selectedPackageId={selectedPackage?.package_id}
            />
          )}
        </>
      )}

      {/* Drawer — chart view only */}
      <PackageLaborDrawer
        open={!!selectedPackage && view === 'chart'}
        pkg={selectedPackage}
        drawerDetail={drawerDetail}
        drawerLoading={drawerLoading}
        weekLabel={formatWeekLabel(weekStart)}
        onClose={() => setSelectedPackage(null)}
      />
    </div>
  );
};

export default Analytics;
